/**
 * The events we host, and where each one's guest list is.
 *
 * A hosted event's guest list is a list under the event, named after it with
 * an ending like "Guests" or "Invitees". The first ending in the order given
 * wins, and the newest list wins a tie (an event can carry a re-import beside
 * the original).
 */

/** The event schemas we host ourselves; the rest (conferences, travel,
 *  holidays) are other people's events on our calendar. */
export const HOSTED_EVENT_SCHEMAS = ["Program Event", "Social/Dinner", "LP Meeting"]

/** The endings an event's guest list goes by, most specific first. */
export const GUEST_LIST_ENDINGS = ["guests", "guest list", "invitees"]

/** The same endings as a case-insensitive pattern, for a query's `_iregex`, so
 *  a read fetches only lists that can be an event's guest list. */
export const GUEST_LIST_PATTERN = `(${GUEST_LIST_ENDINGS.join("|")})$`

interface ChildList {
  id: number
  name: string
  archived_at?: string | null
}

/** An event's guest list: the live child list whose name ends with one of
 *  `endings`, or null. Every reader takes this one list, so a Luma list and its
 *  older Airtable import never count a guest twice. */
export function childListByEnding<L extends ChildList>(
  lists: L[] | null | undefined,
  endings: string[] = GUEST_LIST_ENDINGS
): L | null {
  const live = (lists ?? []).filter((list) => !list.archived_at).sort((a, b) => b.id - a.id)
  for (const ending of endings.map((e) => e.toLowerCase())) {
    const match = live.find((list) => list.name.toLowerCase().endsWith(ending))
    if (match) return match
  }
  return null
}
